import React, { useCallback, useContext } from "react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { ColorStyle } from "src/utils/Colors";
import { RootStoreContext } from "src/stores/RootStore";
import { SCREEN_SIZE } from "src/stores/UIStore";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { Invoice } from "src/stores/krediflow/InvoiceStore";
import { getInvoiceTypeIcon } from "src/screens/creditors/Creditors";
import { formatDate } from "src/stores/krediflow/Utils";
import { formatCurrency } from "src/utils/Currency";
import styles from "./InvoiceListRow.module.css";
import listStyles from "./InvoiceList.module.css";

const InvoiceListRowDesktopBase = (props: { invoice: Invoice; widths: number[] }) => {
    const { uiStore, invoiceListStore, navStore } = useContext(RootStoreContext);

    const { invoiceType, number, date, creditor, invoiceNumber, amount, dueDate, realestate, assignedUser, state } =
        props.invoice;

    const { widths } = props;
    const { t } = useTranslation();

    // OnChange method needed for controlled checkbox component - all the logic is in the onClickCheckbox method because only from there the stopPropagation works (preventing row click event)
    const onChangeDummyMethod = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {}, []);

    const onClickCheckbox = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            e.stopPropagation(); // Prevent row click event

            if (number) {
                invoiceListStore.handleBatchProcessCheckboxClick(number);
            }
        },
        [number, invoiceListStore]
    );

    return (
        <UI.List.Row
            className={`${styles.InvoiceListRow} InvoiceListRow ${
                number && invoiceListStore.batchProcessMap[number] === true ? "Selected" : ""
            }`}
            key={number}
        >
            {/* Batch Processing Checkbox */}
            <UI.List.Cell
                className={`${navStore.hasFeatureKrediFlowPro ? "d-flex justify-content-center" : "d-none"}`}
                value={
                    <input
                        type="checkbox"
                        className={listStyles.BatchProcessCheckbox}
                        onChange={onChangeDummyMethod}
                        onClick={onClickCheckbox}
                        checked={number ? invoiceListStore.batchProcessMap[number] : false}
                    />
                }
                colspan={widths[0]}
            />

            {/* TYPE (ICON) */}
            <UI.List.Cell
                className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP_L ? "d-none" : "d-flex"}`}
                value={
                    <div className={styles.TypeIcon}>
                        <img
                            src={getInvoiceTypeIcon(invoiceType)}
                            alt={t(`screens.kredi_flow.invoice_type.${String(invoiceType)}`) ?? "icon"}
                        />
                    </div>
                }
                colspan={widths[1]}
            />

            {/* NUMBER */}
            <UI.List.Cell value={number} colspan={widths[2]} />

            {/* INVOICE DATE */}
            <UI.List.Cell
                value={
                    date
                        ? uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                            ? formatDate(date)
                            : formatDate(date, true)
                        : "-"
                }
                colspan={widths[3]}
            />

            {/* CREDITOR NAME */}
            <UI.List.Cell colspan={widths[4]} value={creditor?.name ?? ""} truncate />

            {/* INVOICE NUMBER */}
            <UI.List.Cell
                className={uiStore.currentScreenSize <= SCREEN_SIZE.TABLET ? "d-none" : "d-flex"}
                colspan={widths[5]}
                value={invoiceNumber ?? ""}
            />

            {/* AMOUNT */}
            <UI.List.Cell
                // className={`${uiStore.currentScreenSize < SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                colspan={widths[6]}
                value={amount ? formatCurrency(amount) : "-"}
                truncate
            />

            {/* DUE DATE */}
            <UI.List.Cell
                className={uiStore.currentScreenSize <= SCREEN_SIZE.TABLET ? "d-none" : "d-flex"}
                value={
                    dueDate
                        ? uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                            ? formatDate(dueDate)
                            : formatDate(dueDate, true)
                        : "-"
                }
                colspan={widths[7]}
            />

            {/* REALESTATE */}
            <UI.List.Cell
                colspan={widths[8]}
                value={
                    realestate && (
                        <div>
                            <div>{realestate.name}</div>
                            <div>
                                {realestate.zip} {realestate.city}
                            </div>
                            <div>{realestate.number ? `[${realestate.number}]` : ""}</div>
                        </div>
                    )
                }
                truncate
            />

            {/* ASSIGNED USER */}
            <UI.List.Cell
                className={uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}
                value={
                    <div className={styles.CellContainerCenter}>
                        {uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP ? (
                            <UI.Icon
                                icon={UI.SVGIcon.Profile}
                                color={ColorStyle(assignedUser ? "primary" : "yellow")}
                            />
                        ) : undefined}
                        {assignedUser ? `${assignedUser.name1} ${assignedUser.name2}` : "-"}
                    </div>
                }
                colspan={widths[9]}
                truncate
            />

            {/* STATE */}
            <UI.List.Cell
                className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP_L ? "d-none" : "d-flex"}`}
                colspan={widths[10]}
                value={
                    <div className={styles[`status-${state}`]}>{t(`screens.kredi_flow.state.${String(state)}`)}</div>
                }
                truncate
            />
        </UI.List.Row>
    );
};

export const InvoiceListRowDesktop = Sentry.withProfiler(observer(InvoiceListRowDesktopBase));
